/**
 * API Client med Axios
 * Konfigurerad med interceptors för auth tokens
 * Baserat på deque-web-main men anpassad för DQFleet
 * 
 * FÖRBÄTTRAD: Proaktiv token refresh för att undvika utloggning
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { useAuthStore } from '@/store/authStore';

// API ska alltid gå till SAMMA server som sidan när man inte är på localhost.
// Det undviker att prod-frontend (app.dqfleet.se) av misstag anropar test (188).
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return ''; // Same origin – alltid samma server som sidan (153 vid prod, 188 vid test)
    }
  }
  // I dev-läge: använd tom baseURL så att Vite-proxyn fångar /api-requests
  return import.meta.env.VITE_API_BASE_URL || '';
};
const API_BASE_URL = getApiBaseUrl();

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 sekunder
  withCredentials: true, // Se till att cookies skickas med för alla requests
});

// Flag för att undvika race conditions vid proaktiv refresh
let isProactivelyRefreshing = false;
let proactiveRefreshPromise: Promise<string | null> | null = null;

/**
 * Dekoda JWT token för att kolla expiry
 * Returnerar expiry timestamp eller null om token är ogiltig
 */
const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null; // Konvertera till millisekunder
  } catch {
    return null;
  }
};

/**
 * Kontrollera om token behöver förnyas proaktivt
 * Returnerar true om token går ut inom 2 minuter
 */
const shouldProactivelyRefresh = (token: string): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return false;
  
  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  const twoMinutes = 2 * 60 * 1000; // 2 minuter i millisekunder
  
  return timeUntilExpiry < twoMinutes && timeUntilExpiry > 0;
};

/**
 * Request Interceptor
 * Lägger till access token i headers om den finns
 * FÖRBÄTTRAD: Proaktiv token refresh innan token går ut
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    let token = Cookies.get('accessToken');
    
    // Proaktiv refresh: Om token snart går ut och vi har refresh token, förnya FÖRST
    // Skippa om detta är ett refresh-anrop för att undvika loops
    if (token && !config.url?.includes('/auth/refresh')) {
      if (shouldProactivelyRefresh(token)) {
        console.log('[Auth] Token expires soon, proactively refreshing...');
        
        // Undvik parallella refresh-anrop
        if (!isProactivelyRefreshing) {
          isProactivelyRefreshing = true;
          proactiveRefreshPromise = refreshAccessToken();
        }
        
        try {
          const newToken = await proactiveRefreshPromise;
          if (newToken) {
            token = newToken;
            console.log('[Auth] Proactive refresh successful');
          }
        } catch (error) {
          console.warn('[Auth] Proactive refresh failed:', error);
        } finally {
          isProactivelyRefreshing = false;
          proactiveRefreshPromise = null;
        }
      }
    }
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // För FormData, låt axios sätta Content-Type automatiskt (inkluderar boundary)
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

/**
 * Refresh Access Token
 * Anropas när access token är utgången
 */
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = Cookies.get('refreshToken');
    
    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    // Använd api-instansen för att få rätt baseURL
    const response = await api.post('/api/auth/refresh', {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data.data;
    
    // Uppdatera cookies
    Cookies.set('accessToken', accessToken, { expires: 7 });
    Cookies.set('refreshToken', newRefreshToken, { expires: 30 });

    // Uppdatera Zustand store
    const currentState = useAuthStore.getState();
    if (currentState.user) {
      useAuthStore.setState({
        accessToken,
        refreshToken: newRefreshToken,
      });
    }

    return accessToken;
  } catch (error) {
    console.error('Refresh token failed:', error);
    return null;
  }
};

/**
 * Response Interceptor
 * Hanterar 401 errors och försöker refresh token
 */
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Om 401 från själva refresh-anropet – logga ut direkt (undvik loop)
    if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
      useAuthStore.getState().logOutUser();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Om 401 och vi inte redan har försökt refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        
        if (newAccessToken && originalRequest.headers) {
          // Försök igen med ny token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh misslyckades - logga ut användaren
        console.error('Token refresh failed, logging out user');
        useAuthStore.getState().logOutUser();
        
        // Redirect till login (endast i browser)
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Om 401 och refresh misslyckades, logga ut
    if (error.response?.status === 401) {
      useAuthStore.getState().logOutUser();
      
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Background Token Refresh
 * Förnyar token automatiskt var 10:e minut om användaren är inloggad
 * Detta säkerställer att tokens förnyas även om användaren är inaktiv men har sidan öppen
 */
let backgroundRefreshInterval: ReturnType<typeof setInterval> | null = null;

const startBackgroundRefresh = () => {
  // Stoppa eventuellt tidigare intervall
  if (backgroundRefreshInterval) {
    clearInterval(backgroundRefreshInterval);
  }
  
  // Kör var 10:e minut (10 * 60 * 1000 ms)
  backgroundRefreshInterval = setInterval(async () => {
    const token = Cookies.get('accessToken');
    const refreshToken = Cookies.get('refreshToken');
    
    // Endast refresh om användaren är inloggad
    if (token && refreshToken) {
      const expiry = getTokenExpiry(token);
      if (expiry) {
        const timeUntilExpiry = expiry - Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        
        // Refresh om token går ut inom 5 minuter
        if (timeUntilExpiry < fiveMinutes && timeUntilExpiry > 0) {
          console.log('[Auth] Background refresh: Token expires in', Math.round(timeUntilExpiry / 1000), 'seconds');
          await refreshAccessToken();
        }
      }
    }
  }, 10 * 60 * 1000); // Var 10:e minut
};

// Starta background refresh när modulen laddas
if (typeof window !== 'undefined') {
  // Vänta lite så att appen hinner ladda
  setTimeout(startBackgroundRefresh, 5000);
}

export default api;
export { getApiBaseUrl, startBackgroundRefresh };

